import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge class names with Tailwind CSS conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date to a readable string
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  });
}

/**
 * Format a phone number
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// =============================================================================
// Input Formatting Helpers (for live formatting as user types)
// =============================================================================

/**
 * Format phone number as user types: (555) 123-4567
 * Use this for input onChange handlers.
 *
 * @param value - Raw input value (may contain existing formatting)
 * @returns Formatted phone string
 *
 * @example
 * formatPhoneInput('555')        // '555'
 * formatPhoneInput('5551234567') // '(555) 123-4567'
 * formatPhoneInput('(555) 12')   // '(555) 12'
 */
export function formatPhoneInput(value: string): string {
  // Remove all non-digit characters
  let digits = value.replace(/\D/g, '');

  // Strip +1 country code prefix (US) - handles browser autofill
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  // Limit to 10 digits (US phone numbers)
  const limitedDigits = digits.slice(0, 10);
  // Format progressively as user types
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
}

/**
 * Format price as user types: $1,500,000
 * Use this for input value display.
 *
 * @param value - Raw input value (may contain existing formatting or just digits)
 * @returns Formatted price string with $ and commas
 *
 * @example
 * formatPriceInput('1500000')    // '$1,500,000'
 * formatPriceInput('$1,500')     // '$1,500'
 * formatPriceInput('')           // ''
 */
export function formatPriceInput(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Return empty string if no digits
  if (!digits) return '';

  // Parse to number and format with commas
  const number = parseInt(digits, 10);
  return `$${number.toLocaleString('en-US')}`;
}

/**
 * Parse formatted price back to digits-only string.
 * Use this for input onChange handlers to store clean value.
 *
 * @param value - Formatted price string (e.g., '$1,500,000')
 * @returns Digits-only string (e.g., '1500000')
 *
 * @example
 * parsePriceInput('$1,500,000') // '1500000'
 * parsePriceInput('1500')       // '1500'
 */
export function parsePriceInput(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Format a relative time string (e.g., "2 min ago", "1 hour ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  // For older dates, show the actual date
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
